import { Improvement } from '../../improvement'

import { enums } from '../../../gads'
import { Targets } from '../../..'

export type ExtraDetails = {
    cpc_bid: number
    resource_name: string
}[]

export interface LowVolumeBody {
    campaign_group: string
    bidding_strategy_type: enums.BiddingStrategyType
    campaign_group_cpa: number
    campaign_type: enums.AdvertisingChannelType
    campaign_id: number
    campaign_name: string
    campaign_cost: number
    campaign_conversions: number
    campaign_impressions: number
    campaign_conversions_value: number
    target: number
    target_set: boolean
    performance_mode: Targets.PerformanceMode
    window: Improvement.LookbackWindow
    demographic: AllowedCriteriaTypes
    placement: SnakeCaseKeywordPlacement
    criteria: number
    number_of_items_in_placement: number
    minimum_clicks: number
    ad_group_resource_name: string
    ad_group_placement_clicks: number
    ad_group_placement_cost: number
    ad_group_placement_impressions: number
    ad_group_placement_conversions_raw: number
    ad_group_placement_conversion_value_raw: number
    ad_group_placement_view_through_conv: number
    ad_group_placement_all_conversions_value: number
    ad_group_placement_conversions: number
    ad_group_placement_roas: number
    ad_group_placement_cpa: number
    ad_group_cost: number
    ad_group_conversions: number
    ad_group_impressions: number
    ad_group_conversions_value: number
    ad_group_conversions_raw: number
    ad_group_conversion_value_raw: number
    ad_group_view_through_conv: number
    ad_group_cpa: number
    ad_group_roas: number
    new_bid_mod: number
    is_increase: boolean
    bid_valid: boolean
    invalid: boolean
    reason: string
    items_in_placement: { resource_name: string; name: string }[]
    is_exclude?: boolean
}

export interface HighVolumeBody {
    ad_group_resource_name: string
    bidding_strategy_type: enums.BiddingStrategyType
    campaign_group: string
    campaign_group_cpa: number
    campaign_type: enums.AdvertisingChannelType
    campaign_name: string
    campaign_id: number
    target: number
    target_set: boolean
    performance_mode: Targets.PerformanceMode
    demographic: AllowedCriteriaTypes
    placement: SnakeCaseKeywordPlacement
    criteria: number
    ad_group_id: number
    ad_group: string
    ad_group_cost: number
    ad_group_impressions: number
    ad_group_conversions: number
    ad_group_conversions_value: number
    ad_group_conversions_raw: number
    ad_group_conversion_value_raw: number
    ad_group_view_through_conv: number
    ad_group_cpa: number
    ad_group_roas: number
    resource_name: string
    ad_group_placement_cost: number
    ad_group_placement_clicks: number
    current_bid: number
    ad_group_placement_conversions: number
    ad_group_placement_impressions: number
    ad_group_placement_all_conversions_value: number
    ad_group_placement_conversions_raw: number
    ad_group_placement_conversion_value_raw: number
    ad_group_placement_view_through_conv: number
    window: Improvement.LookbackWindow
    ad_group_placement_roas: number
    ad_group_placement_cpa: number
    minimum_clicks: number
    passes_minimum_clicks: boolean
    new_bid_mod: number
    is_increase: boolean
    bid_valid: boolean
    invalid: boolean
    reason: string
    is_exclude?: boolean
}

export type DemoBodies = (HighVolumeBody | LowVolumeBody)[]

export type SnakeCaseAllowedCriteriaTypes =
    | AllowedCriteriaTypes.AGE
    | AllowedCriteriaTypes.GENDER
    | AllowedCriteriaTypes.PARENTAL_STATUS
    | AllowedCriteriaTypes.INCOME

export enum AllowedCriteriaTypes {
    AGE = 'age',
    GENDER = 'gender',
    PARENTAL_STATUS = 'parental_status',
    INCOME = 'income',
}

export type SnakeCaseKeywordPlacement =
    | KeywordPlacement.FEMALE
    | KeywordPlacement.MALE
    | KeywordPlacement.UNDETERMINED
    | KeywordPlacement.UNSPECIFIED
    | KeywordPlacement.UNKNOWN
    | KeywordPlacement.PARENT
    | KeywordPlacement.NOT_A_PARENT
    | KeywordPlacement.INCOME_RANGE_0_50
    | KeywordPlacement.INCOME_RANGE_50_60
    | KeywordPlacement.INCOME_RANGE_60_70
    | KeywordPlacement.INCOME_RANGE_70_80
    | KeywordPlacement.INCOME_RANGE_80_90
    | KeywordPlacement.INCOME_RANGE_90_UP
    | KeywordPlacement.INCOME_RANGE_UNDETERMINED
    | KeywordPlacement.AGE_RANGE_18_24
    | KeywordPlacement.AGE_RANGE_25_34
    | KeywordPlacement.AGE_RANGE_35_44
    | KeywordPlacement.AGE_RANGE_45_54
    | KeywordPlacement.AGE_RANGE_55_64
    | KeywordPlacement.AGE_RANGE_65_UP
    | KeywordPlacement.AGE_RANGE_UNDETERMINED

export enum KeywordPlacement {
    FEMALE = 'Female',
    MALE = 'Male',
    UNDETERMINED = 'Undetermined',
    UNSPECIFIED = 'Unspecified',
    UNKNOWN = 'Unknown',
    PARENT = 'Parent',
    NOT_A_PARENT = 'Not a parent',
    INCOME_RANGE_0_50 = 'Lower 50%',
    INCOME_RANGE_50_60 = '41-50%',
    INCOME_RANGE_60_70 = '31-40%',
    INCOME_RANGE_70_80 = '21-30%',
    INCOME_RANGE_80_90 = '11-20%',
    INCOME_RANGE_90_UP = 'Top 10%',
    INCOME_RANGE_UNDETERMINED = 'income_Unknown',
    AGE_RANGE_18_24 = '18 to 24',
    AGE_RANGE_25_34 = '25 to 34',
    AGE_RANGE_35_44 = '35 to 44',
    AGE_RANGE_45_54 = '45 to 54',
    AGE_RANGE_55_64 = '55 to 64',
    AGE_RANGE_65_UP = '65+',
    AGE_RANGE_UNDETERMINED = 'age_Unknown',
}

export enum InvalidBidReasonRaw {
    BID_CHANGE_IS_TOO_SMALL = 'BID_CHANGE_IS_TOO_SMALL',
    BID_IN_WRONG_DIRECTION = 'BID_IN_WRONG_DIRECTION',
    BUDGET_CAPPED_CONSTRAINTS = `WON'T_BID_UP_FOR_ALREADY_BUDGET_CONSTRAINED_CAMPAIGNS`,
    MINIMUM_CLICKS_NOT_MET = `MINIMUM_CLICKS_NOT_MET`,
    EXCLUDED = 'SEGMENT_EXCLUDED',
    PLACEMENT_COST_OVER_ENVIRONMENT_THRESHOLD = 'PLACEMENT_COST_OVER_ENVIRONMENT_THRESHOLD',
}

export function instanceOfAdGroup(object: any): object is HighVolumeBody {
    return !object?.items_in_placement
}

export function instanceOfAdGroupArray(objectArray: any[]): objectArray is HighVolumeBody[] {
    return !objectArray?.[0]?.items_in_placement
}

export function instanceOfCampaignArray(objectArray: any[]): objectArray is LowVolumeBody[] {
    return objectArray?.[0]?.items_in_placement
}
